/**
 * AP-23 (Phase D) — Abschnitt „Listen-Standards" in den Mandanten-Einstellungen.
 *
 * Bewusst nur eine **Übersicht**: Gesetzt wird ein Standard aus der Liste heraus
 * („Aktuelles Layout als Mandanten-Standard speichern"), denn niemand tippt hier
 * Spaltenschlüssel ab. Diese Maske beantwortet zwei andere Fragen — was ist
 * überhaupt gesetzt, und wer hat es wann gesetzt — und erlaubt das Entfernen.
 *
 * Nicht zu verwechseln mit dem Menüpunkt *Listen* unter Zeiterfassung →
 * Einrichtung: Das sind Auswahllisten (Betriebsstätten, Gruppen, Feldoptionen).
 * Hier geht es um Spalten- und Suchfeld-Vorgaben der Auswertungslisten.
 */

import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { Trash2, Info } from 'lucide-react'
import { fetchTenantDefaults, deleteTenantDefault, type TenantUiDefault } from '@/lib/ui-settings'
import { fmtDateTime } from '@/lib/format-date'

/**
 * Anzeigenamen der Schlüssel. `…_payroll` gehört zu den Tagesarbeitszeiten — der
 * Schlüssel blieb bei der Umbenennung (KA-14) absichtlich stehen, weil ein
 * Umbenennen alle gespeicherten Layouts verworfen hätte.
 */
const LIST_LABELS: Record<string, string> = {
  summary: 'Zusammenfassung',
  payroll: 'Tagesarbeitszeiten',
  payroll_raster: 'Lohnverrechnung — Monatserfassung',
  absences: 'Fehlzeiten',
  projects: 'Projekte',
}

const KIND_LABELS: Record<string, string> = {
  columnbar_layout: 'Spalten',
  filterbar_layout: 'Suchfelder',
  pagesize: 'Zeilen pro Seite',
}

function describeKey(key: string): { list: string; kind: string } {
  const match = /^(columnbar_layout|filterbar_layout|pagesize)_(.+)$/.exec(key)
  if (!match) return { list: key, kind: '' }
  const [, kind, list] = match
  return { list: LIST_LABELS[list] ?? list, kind: KIND_LABELS[kind] ?? kind }
}

/**
 * Kurzfassung des Werts. Bewusst nur die Anzahl statt der Schlüssel: Die heißen
 * `activityType`, `cf_…`, `lv_…` und sind für einen Admin nichts als Rauschen —
 * was genau drinsteht, sieht er in der Liste selbst.
 */
function summarize(key: string, value: unknown, t: TFunction): string {
  if (key.startsWith('pagesize_')) return String(value)
  const v = value as { columnKeys?: string[]; fieldKeys?: string[] } | null
  if (Array.isArray(v?.columnKeys)) {
    return t('uiSettings.columnCount', {
      count: v.columnKeys.length,
      defaultValue: '{{count}} Spalten',
    })
  }
  if (Array.isArray(v?.fieldKeys)) {
    return t('uiSettings.fieldCount', {
      count: v.fieldKeys.length,
      defaultValue: '{{count}} Suchfelder',
    })
  }
  return '–'
}

export function ListDefaultsSection() {
  const { t } = useTranslation()
  const [rows, setRows] = useState<TenantUiDefault[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [busyKey, setBusyKey] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(false)
    try {
      setRows(await fetchTenantDefaults())
    } catch {
      setError(true)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  const handleRemove = async (key: string) => {
    setBusyKey(key)
    try {
      await deleteTenantDefault(key)
      setRows((prev) => prev.filter((r) => r.key !== key))
    } catch {
      setError(true)
    } finally {
      setBusyKey(null)
    }
  }

  return (
    <div className="rounded-lg border border-border bg-card p-6 shadow-sm">
      <h2 className="mb-1 text-base font-semibold text-card-foreground">
        {t('uiSettings.listDefaults', { defaultValue: 'Listen-Standards' })}
      </h2>
      <p className="mb-4 text-sm text-muted-foreground">
        {t('uiSettings.listDefaultsIntro', {
          defaultValue:
            'Spalten und Suchfelder, mit denen neue Mitarbeiter starten. Gesetzt wird ein Standard direkt in der jeweiligen Liste über „Als Mandanten-Standard speichern".',
        })}
      </p>

      {loading ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : error ? (
        <p className="text-sm text-destructive">
          {t('uiSettings.loadFailed', { defaultValue: 'Standards konnten nicht geladen werden.' })}
        </p>
      ) : rows.length === 0 ? (
        <div className="flex items-start gap-2 rounded-md border border-border bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            {t('uiSettings.noListDefaults', {
              defaultValue:
                'Noch kein Standard gesetzt — alle starten mit der Vorbelegung der jeweiligen Liste.',
            })}
          </span>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border">
              <tr className="text-left text-xs font-medium text-muted-foreground">
                <th className="px-2 py-1.5">{t('uiSettings.list', { defaultValue: 'Liste' })}</th>
                <th className="px-2 py-1.5">{t('uiSettings.kind', { defaultValue: 'Bereich' })}</th>
                <th className="px-2 py-1.5">{t('uiSettings.content', { defaultValue: 'Inhalt' })}</th>
                <th className="px-2 py-1.5">{t('uiSettings.setBy', { defaultValue: 'Gesetzt von' })}</th>
                <th className="px-2 py-1.5"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const { list, kind } = describeKey(row.key)
                return (
                  <tr key={row.key} className="border-b border-border/60 last:border-0">
                    <td className="px-2 py-2 font-medium text-foreground">{list}</td>
                    <td className="px-2 py-2 text-muted-foreground">{kind}</td>
                    <td className="px-2 py-2 text-muted-foreground">{summarize(row.key, row.value, t)}</td>
                    <td className="px-2 py-2 text-muted-foreground">
                      {row.updatedByName ?? '–'}{' '}
                      <span className="text-xs">({fmtDateTime(row.updatedAt)})</span>
                    </td>
                    <td className="px-2 py-2 text-right">
                      <button
                        type="button"
                        disabled={busyKey === row.key}
                        onClick={() => handleRemove(row.key)}
                        className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs text-muted-foreground hover:bg-muted hover:text-destructive disabled:opacity-50"
                        title={t('uiSettings.removeDefaultHint', {
                          defaultValue:
                            'Entfernt nur den Startwert. Wer bereits eine eigene Einstellung hat, behält sie.',
                        })}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        {t('common.remove', { defaultValue: 'Entfernen' })}
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
